import React, { useState } from 'react'
import Accordion, { AccordionItem } from 'src/components/ui/Accordion'
import '../../filter.scss'
import './facets.scss'
import useWindowDimensions from 'src/hooks/useWindowDimensions'

import PriceRange from '../PriceRange'
import FilterQuantity from '../FilterQuantity'
import FilterHeader from '../FilterHeader'
import PaginationFilter from '../PaginationFilter'
import useFilterWithMoreFacets from '../../hooks/useFilterWithMoreFacets'
import FilterMore from '../FilterMore'
import type { FacetsProps } from '../../types/components/Facets'
import listWithImages from '../FilterQuantity/data/listWithImages.json'
import type { IListWithImages } from '../FilterQuantity/FilterQuantity'

function Facets({
  testId,
  selectedFacets,
  filteredFacets,
  indicesExpanded,
  onFacetChange,
  cleanUpFacets,
  onAccordionChange,
  onApply,
  onAccordionItemMount,
  minPriceSelected,
  maxPriceSelected,
  setMinPriceSelected,
  setMaxPriceSelected,
  dismissTransition,
  targetRef,
}: FacetsProps) {
  const listKeysWithImages = listWithImages as IListWithImages
  const { width } = useWindowDimensions()
  const [isFallbackImgFilter, setIsFallbackImgFilter] = useState<
    Array<{
      label: string
      index: string
    }>
  >([])

  const priceFilterVisibility = 'block'

  const isDesktop = width >= 1279

  filteredFacets = filteredFacets?.filter(
    (fragment) =>
      fragment?.key?.toLocaleLowerCase() !== 'monte-sua-pulseira' &&
      fragment?.key?.toLocaleLowerCase() !== 'monte-sua-pulseira-acessorios'
  )

  const [currentPage, setCurrentPage] = useState(1)

  const testFacets = (facetValue: string, itemValue: string) =>
    facetValue?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '') === itemValue

  const useFilteredFacets = useFilterWithMoreFacets(filteredFacets)

  return (
    <div className="filter" data-fs-filter data-testid={testId} ref={targetRef}>
      {useFilteredFacets?.updateFilteredFacets && (
        <Accordion
          expandedIndices={indicesExpanded}
          onChange={onAccordionChange}
        >
          {useFilteredFacets?.updateFilteredFacets?.map((item, index) => {
            const { label, key } = item
            const itemsPerPage = listKeysWithImages?.[key] ? 6 : 15
            const values =
              item.__typename === 'StoreFacetBoolean' ? item.values : []

            const totalPages = Math.ceil(values.length / itemsPerPage)
            const currentPageNew =
              currentPage > totalPages ? totalPages : currentPage

            const valuesRender =
              totalPages > 1
                ? values.slice(
                    (currentPageNew - 1) * itemsPerPage,
                    currentPageNew * itemsPerPage
                  )
                : values

            const min =
              item.__typename === 'StoreFacetRange' ? item.min : undefined

            const max =
              item.__typename === 'StoreFacetRange' ? item.max : undefined

            if (key === 'price') {
              const listPriceSelected = selectedFacets?.filter(
                (facetSelected) => facetSelected?.key === 'price'
              )?.length

              return (
                <AccordionItem
                  key={`${label}-${index}`}
                  prefixId={testId}
                  testId={`${testId}-accordion`}
                  isExpanded={indicesExpanded?.has(index)}
                  buttonLabel={label}
                  expandedBackgroundSelected
                  style={{ display: `${priceFilterVisibility}` }}
                  addicionalLabel={
                    listPriceSelected > 0 ? listPriceSelected?.toString() : ''
                  }
                >
                  <>
                    {min && max && (
                      <PriceRange
                        min={min}
                        max={max}
                        minPriceSelected={minPriceSelected}
                        maxPriceSelected={maxPriceSelected}
                        setMinPriceSelected={setMinPriceSelected}
                        setMaxPriceSelected={setMaxPriceSelected}
                        selectedFacets={selectedFacets}
                      />
                    )}

                    <FilterHeader
                      index={index}
                      onApply={onApply}
                      cleanUpFacets={cleanUpFacets}
                      isDesktop={isDesktop}
                      onAccordionChange={onAccordionChange}
                      type="price"
                      dismissTransition={dismissTransition}
                    />
                  </>
                </AccordionItem>
              )
            }

            if (key === 'filterMore') {
              return (
                <FilterMore
                  key={`${label}-${index}`}
                  label={label}
                  useFilteredFacets={useFilteredFacets}
                />
              )
            }

            const itemsSelected = valuesRender?.filter(
              (value: { selected: boolean }) => value.selected
            ).length

            return (
              <AccordionItem
                key={`${label}-${index}`}
                prefixId={testId}
                testId={`${testId}-accordion`}
                isExpanded={indicesExpanded?.has(index)}
                buttonLabel={label}
                expandedBackgroundSelected
                addicionalLabel={
                  itemsSelected > 0 ? itemsSelected?.toString() : ''
                }
                ref={(_) => onAccordionItemMount(index, valuesRender)}
                style={{
                  display: `${
                    useFilteredFacets?.status &&
                    index > useFilteredFacets?.indexHide &&
                    !useFilteredFacets?.moreFilter
                      ? 'none'
                      : ''
                  }`,
                }}
              >
                <FilterQuantity
                  keyValue={key}
                  values={valuesRender}
                  testId={testId}
                  label={label}
                  selectedFacets={selectedFacets}
                  testFacets={testFacets}
                  onFacetChange={onFacetChange}
                  isFallbackImgFilter={isFallbackImgFilter}
                  setIsFallbackImgFilter={setIsFallbackImgFilter}
                />
                {values?.length > itemsPerPage && (
                  <PaginationFilter
                    currentPage={currentPageNew}
                    totalPages={totalPages}
                    isExpanded={indicesExpanded?.has(index)}
                    setCurrentPage={setCurrentPage}
                    isDesktop={isDesktop}
                    label={label}
                    setIsFallbackImgFilter={setIsFallbackImgFilter}
                  />
                )}
                <FilterHeader
                  index={index}
                  onApply={onApply}
                  cleanUpFacets={cleanUpFacets}
                  isDesktop={isDesktop}
                  onAccordionChange={onAccordionChange}
                  selectedFacets={selectedFacets}
                  type="quantity"
                  dismissTransition={dismissTransition}
                />
              </AccordionItem>
            )
          })}
        </Accordion>
      )}
    </div>
  )
}

export default Facets
